/**
 * Was mit einer Meldung passiert ist (F-30): gelesen, erledigt, verschoben -
 * und was daraus fuer die Glocke und den Push-Plan (F-31) folgt.
 */
import { nachRuhezeit, type Meldung } from '@/domain/benachrichtigungen/regeln';
import { addDays, addMinutes, datePart } from '@/lib/datetime';
import type { LocalDate, LocalDateTime } from '@/domain/types';

export interface MeldungsZustand {
  id: string;
  /** Tag der Meldung - zum Aufraeumen. */
  datum: LocalDate;
  gelesen?: 0 | 1;
  erledigt?: 0 | 1;
  verschobenBis?: LocalDateTime;
}

export type Verschieben = '1h' | 'morgen';

export const VERSCHIEBEN_LABEL: Record<Verschieben, string> = { '1h': 'In 1 Stunde', morgen: 'Morgen' };

export function verschiebenBis(wie: Verschieben, jetzt: LocalDateTime, ruheVon: string, ruheBis: string): LocalDateTime {
  const ziel = wie === '1h' ? addMinutes(jetzt, 60) : (`${addDays(datePart(jetzt), 1)}T08:00` as LocalDateTime);
  return nachRuhezeit(ziel, ruheVon, ruheBis);
}

const wirksam = (m: Meldung, z?: MeldungsZustand): Meldung => (z?.verschobenBis ? { ...m, zeit: z.verschobenBis } : m);

/** Fuer die Glocke: faellig, nicht erledigt, neueste zuerst. */
export function aktuelle(ms: Meldung[], zustand: Map<string, MeldungsZustand>, jetzt: LocalDateTime): { m: Meldung; gelesen: boolean }[] {
  return ms
    .map((m) => ({ m: wirksam(m, zustand.get(m.id)), z: zustand.get(m.id) }))
    .filter(({ m, z }) => m.zeit <= jetzt && !z?.erledigt)
    .sort((a, b) => b.m.zeit.localeCompare(a.m.zeit))
    .map(({ m, z }) => ({ m, gelesen: !!z?.gelesen }));
}

export interface PlanEintrag {
  id: string;
  zeit: LocalDateTime;
  titel: string;
  text: string;
  link: string;
}

/** Ab so vielen gleichzeitigen Meldungen kommt eine gesammelte (nach der Ruhezeit). */
const SAMMELN_AB = 3;

/**
 * Fuer Push (F-31): kuenftige, nicht erledigte Meldungen. Was zur selben
 * Minute faellig ist (typisch: das Ende der Ruhezeit), wird ab drei zu einer
 * Nachricht zusammengefasst. Mit `neutral` ohne Titel und Inhalte.
 */
export function pushPlan(ms: Meldung[], zustand: Map<string, MeldungsZustand>, jetzt: LocalDateTime, neutral: boolean): PlanEintrag[] {
  const kuenftig = ms.map((m) => ({ m: wirksam(m, zustand.get(m.id)), z: zustand.get(m.id) }))
    .filter(({ m, z }) => m.zeit > jetzt && !z?.erledigt).map(({ m }) => m);
  const nachZeit = new Map<string, Meldung[]>();
  for (const m of kuenftig) nachZeit.set(m.zeit, [...(nachZeit.get(m.zeit) ?? []), m]);
  const out: PlanEintrag[] = [];
  for (const [zeit, gruppe] of nachZeit) {
    if (gruppe.length >= SAMMELN_AB) {
      out.push({
        id: `sammel:${zeit}:${kurzHash(gruppe.map((m) => m.id).join('|'))}`, zeit,
        titel: `${gruppe.length} Hinweise`,
        text: gruppe.map((m) => (neutral ? m.neutral : m.titel)).join(' · '),
        link: '/?meldungen=1',
      });
    } else {
      for (const m of gruppe) out.push({ id: m.id, zeit, titel: neutral ? m.neutral : m.titel, text: neutral ? '' : m.text, link: m.link });
    }
  }
  return out.sort((a, b) => a.zeit.localeCompare(b.zeit));
}

/** Kurzer, stabiler Fingerabdruck (djb2) - eine neue Gruppe gibt eine neue ID. */
function kurzHash(text: string): string {
  let h = 5381;
  for (let i = 0; i < text.length; i++) h = ((h * 33) ^ text.charCodeAt(i)) >>> 0;
  return h.toString(36);
}
